import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Currency conversion rates (base: AUD)
const CURRENCIES = [
    { code: 'aud', symbol: 'A$', name: 'Australian Dollar', rate: 1.0, flag: '🇦🇺' },
    { code: 'usd', symbol: '$', name: 'US Dollar', rate: 0.65, flag: '🇺🇸' },
    { code: 'gbp', symbol: '£', name: 'British Pound', rate: 0.52, flag: '🇬🇧' },
    { code: 'eur', symbol: '€', name: 'Euro', rate: 0.60, flag: '🇪🇺' },
];

// Get currency from localStorage or default to AUD
export const getStoredCurrency = () => {
    if (typeof window !== 'undefined') {
        return localStorage.getItem('coregulate_currency') || 'aud';
    }
    return 'aud';
};

// Save currency to localStorage
export const setStoredCurrency = (code) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem('coregulate_currency', code);
    }
};

// Get currency info by code
export const getCurrencyInfo = (code) => {
    return CURRENCIES.find(c => c.code === code) || CURRENCIES[0];
};

// Convert price from AUD to selected currency
export const convertPrice = (audPrice, currencyCode) => {
    const currency = getCurrencyInfo(currencyCode);
    return Math.round(audPrice * currency.rate * 100) / 100;
};

// Format price with currency symbol
export const formatPrice = (audPrice, currencyCode, showDecimals = true) => {
    const currency = getCurrencyInfo(currencyCode);
    const converted = convertPrice(audPrice, currencyCode);
    
    if (showDecimals) {
        return `${currency.symbol}${converted.toFixed(2)}`;
    }
    return `${currency.symbol}${Math.round(converted)}`;
};

// Currency Selector Component
export const CurrencySelector = ({ value, onChange, variant = 'default', className = '' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    
    const selectedCurrency = getCurrencyInfo(value);
    
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const handleSelect = (code) => {
        onChange(code);
        setStoredCurrency(code);
        setIsOpen(false);
    };
    
    // Compact variant for headers
    if (variant === 'compact') {
        return (
            <div className={`relative ${className}`} ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-[#7D8E95] hover:text-[#1D6B6B] transition-colors rounded-lg hover:bg-[#1D6B6B]/5"
                    data-testid="currency-selector-compact"
                >
                    <Globe className="w-4 h-4" />
                    <span>{selectedCurrency.code.toUpperCase()}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isOpen && (
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-[#E8E1D6] py-1 min-w-[160px] z-50">
                        {CURRENCIES.map((currency) => (
                            <button
                                key={currency.code}
                                onClick={() => handleSelect(currency.code)}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#FEF6EF] transition-colors ${
                                    value === currency.code ? 'text-[#1D6B6B] bg-[#1D6B6B]/5' : 'text-[#3D5A5A]'
                                }`}
                                data-testid={`currency-option-${currency.code}`}
                            >
                                <span>{currency.flag}</span>
                                <span className="flex-1 text-left">{currency.symbol} {currency.code.toUpperCase()}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
    
    // Default variant - full dropdown
    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between gap-3 w-full px-4 py-3 bg-white border border-[#E8E1D6] rounded-xl hover:border-[#1D6B6B]/30 transition-colors"
                data-testid="currency-selector"
            >
                <div className="flex items-center gap-2">
                    <span className="text-lg">{selectedCurrency.flag}</span>
                    <div className="text-left">
                        <p className="text-sm font-medium text-[#1D3D3D]">{selectedCurrency.name}</p>
                        <p className="text-xs text-[#7D8E95]">{selectedCurrency.symbol} {selectedCurrency.code.toUpperCase()}</p>
                    </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-[#7D8E95] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isOpen && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-[#E8E1D6] py-2 z-50">
                    {CURRENCIES.map((currency) => (
                        <button
                            key={currency.code}
                            onClick={() => handleSelect(currency.code)}
                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FEF6EF] transition-colors ${
                                value === currency.code ? 'bg-[#1D6B6B]/5' : ''
                            }`}
                            data-testid={`currency-option-${currency.code}`}
                        >
                            <span className="text-lg">{currency.flag}</span>
                            <div className="flex-1 text-left">
                                <p className={`text-sm font-medium ${value === currency.code ? 'text-[#1D6B6B]' : 'text-[#1D3D3D]'}`}>
                                    {currency.name}
                                </p>
                                <p className="text-xs text-[#7D8E95]">{currency.symbol} {currency.code.toUpperCase()}</p>
                            </div>
                            {value === currency.code && (
                                <div className="w-2 h-2 rounded-full bg-[#1D6B6B]" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CurrencySelector;
